import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CTA_URL, PHOTOS, HANDLE } from "@/lib/copy";
import { ArrowUpRight, Instagram, Crown } from "lucide-react";

export default function Hero() {
  return (
    <section
      className="relative w-full overflow-hidden bg-[var(--bg)]"
      data-testid="hero-section"
    >
      <div className="grid min-h-[100svh] grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
        {/* IMAGE — top on mobile, right on desktop */}
        <div className="relative order-1 h-[50svh] w-full md:order-2 md:h-auto md:min-h-[100svh]">
          <img
            src={PHOTOS.hero}
            alt="Julssy"
            className="absolute inset-0 h-full w-full object-cover object-[50%_30%] md:object-[50%_38%]"
            draggable={false}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#050505] to-transparent md:hidden" />
          <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-40 bg-gradient-to-r from-[#050505] to-transparent md:block" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#050505]/70 to-transparent md:hidden" />

          {/* Mobile chip + handle */}
          <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-between px-5 pt-5 md:hidden">
            <span className="chip" data-testid="hero-status-chip">
              <span className="dot" />
              online agora
            </span>
            <a
              href="https://www.instagram.com/julssy__/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-super text-white/85 transition hover:text-white"
              data-testid="hero-instagram-link"
            >
              <Instagram className="h-3.5 w-3.5" strokeWidth={2.2} />
              {HANDLE}
            </a>
          </div>
        </div>

        {/* CONTENT — below on mobile, left on desktop */}
        <div className="relative order-2 flex flex-col justify-between px-5 pb-5 pt-4 md:order-1 md:px-12 md:py-10">
          {/* Desktop chip */}
          <div className="hidden items-center justify-between md:flex">
            <span className="chip">
              <span className="dot" />
              online agora
            </span>
            <a
              href="https://www.instagram.com/julssy__/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-super text-white/75 transition hover:text-white"
            >
              <Instagram className="h-3.5 w-3.5" strokeWidth={2.2} />
              {HANDLE}
            </a>
          </div>

          <div className="max-w-xl md:mb-4">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.2, 0.9, 0.2, 1] }}
              className="mb-3 flex items-center gap-3 md:mb-4"
            >
              <span className="h-px w-8 bg-[var(--pink)] md:w-10" />
              <span className="font-mono text-[10px] uppercase tracking-super text-[var(--pink)] md:text-[11px]">
                Julssy · fatalfans
              </span>
            </motion.div>

            <h1 className="font-serif leading-[0.9] tracking-[-0.02em] text-white">
              <span className="mask-line">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.85, delay: 0.1, ease: [0.7, 0, 0.2, 1] }}
                  className="block text-[10.5vw] leading-[0.9] md:text-7xl lg:text-8xl"
                >
                  Descubra
                </motion.span>
              </span>
              <span className="mask-line">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.85, delay: 0.22, ease: [0.7, 0, 0.2, 1] }}
                  className="block text-[10.5vw] italic leading-[0.9] text-[var(--pink)] md:text-7xl lg:text-8xl"
                >
                  meus segredos.
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.5 }}
              className="mt-3 max-w-md font-mono text-[13px] leading-relaxed text-white/75 md:mt-6 md:text-sm"
            >
              Conteúdo exclusivo, sem censura. Fotos, vídeos e chat privado — só pra você.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.5 }}
              className="mt-5 flex flex-col gap-3 md:mt-7 md:flex-row md:flex-wrap md:items-center md:gap-4"
            >
              <a
                href={CTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-magnetic w-full justify-center md:w-auto"
                data-testid="hero-cta-button"
              >
                <span>Acessar meu conteúdo</span>
                <ArrowUpRight className="arrow h-4 w-4" strokeWidth={2.4} />
              </a>
            </motion.div>

            {/* VIP secondary CTA — prominent, breathing room */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.5 }}
              className="mt-3 md:mt-4"
            >
              <Link
                to="/vip"
                className="group relative flex items-center justify-between gap-3 border border-[var(--pink)]/50 bg-[var(--pink)]/[0.08] px-4 py-4 transition-all hover:border-[var(--pink)] hover:bg-[var(--pink)]/[0.14] md:px-5 md:py-4"
                data-testid="hero-vip-cta"
              >
                <span className="flex items-center gap-3">
                  <Crown
                    className="h-5 w-5 shrink-0 text-[var(--pink)] transition-transform group-hover:-rotate-6"
                    strokeWidth={2.2}
                  />
                  <span className="flex flex-col leading-tight">
                    <span className="font-mono text-[10px] uppercase tracking-super text-[var(--pink)] md:text-[11px]">
                      Grupo VIP
                    </span>
                    <span className="font-mono text-[12px] text-white/90 md:text-[13px]">
                      Receba ofertas exclusivas antes de todo mundo
                    </span>
                  </span>
                </span>
                <ArrowUpRight
                  className="h-4 w-4 shrink-0 text-white/70 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[var(--pink)]"
                  strokeWidth={2.2}
                />
              </Link>
            </motion.div>
          </div>

          <div className="hidden md:block" />
        </div>
      </div>
    </section>
  );
}
